import * as React from "react";
import { useStaticQuery, graphql } from "gatsby";

import Header from "./header";
import { ContactModalProvider } from "./contact-modal-context";
import ContactModal from "./ContactModal";
import IntroOverlay from "./IntroOverlay";

import "./layout.css";
import "./intro.css";

/* ──────────────────────────────────────────────────────────────
   Intro constants
   ────────────────────────────────────────────────────────────── */

const INTRO_KEY = "kika_intro_seen_this_session";

// Event fired by Header when user clicks site title
const INTRO_REQUEST_REPLAY_EVENT = "kika:intro:request-replay";

// Event fired by Layout when intro is replaying
const INTRO_REPLAY_EVENT = "kika:intro:replay";

// Event fired when intro fully finishes
const INTRO_FINISHED_EVENT = "intro:finished";

// Tune this to match your overlay animation timing
const HEADER_DELAY_MS = 3800;

/* ──────────────────────────────────────────────────────────────
   iOS-safe: temporarily disable scroll snap while we force scroll
   (requires CSS: html.no-snap, html.no-snap body { scroll-snap-type:none !important; })
   ────────────────────────────────────────────────────────────── */
function disableSnapTemporarily(ms = 600) {
  const html = document.documentElement;
  html.classList.add("no-snap");
  window.setTimeout(() => html.classList.remove("no-snap"), ms);
}

/* ──────────────────────────────────────────────────────────────
   Force scroll to the first work panel (iOS Safari safe)
   - Resets both scrolling roots
   - Disables snap briefly so Safari doesn’t “pull” us elsewhere
   - Uses block:"center" to match your scroll-snap-align:center panels
   ────────────────────────────────────────────────────────────── */
function lockToFirstWorkPanel(lockMs = 1200) {
  if (typeof window === "undefined") return;

  const start = Date.now();
  let rafId = null;
  let intervalId = null;

  const run = () => {
    disableSnapTemporarily(700);

    // hard reset both scrolling roots (Safari varies)
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);

    const firstPanel = document.querySelector(".work-panel");
    if (firstPanel) {
      // match your snap alignment
      firstPanel.scrollIntoView({ behavior: "auto", block: "start" });
    }
  };

  // do it immediately
  run();

  // keep doing it briefly to beat delayed iOS restoration
  intervalId = window.setInterval(() => {
    if (Date.now() - start > lockMs) {
      window.clearInterval(intervalId);
      intervalId = null;
      return;
    }
    run();
  }, 60);

  const tick = () => {
    if (Date.now() - start > lockMs) {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = null;
      return;
    }
    run();
    rafId = window.requestAnimationFrame(tick);
  };

  rafId = window.requestAnimationFrame(tick);

  // cleanup safety
  window.setTimeout(() => {
    if (intervalId) window.clearInterval(intervalId);
    if (rafId) window.cancelAnimationFrame(rafId);
  }, lockMs + 150);
}

/* ──────────────────────────────────────────────────────────────
   Detect hard refresh
   ────────────────────────────────────────────────────────────── */
function wasReloadNavigation() {
  try {
    const navEntries = performance.getEntriesByType?.("navigation");
    if (navEntries?.length) {
      return navEntries[0].type === "reload";
    }
  } catch (_) {}

  try {
    return performance.navigation?.type === 1;
  } catch (_) {}

  return false;
}

export default function Layout({ children }) {
  const data = useStaticQuery(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          title
        }
      }
    }
  `);

  const siteTitle = data.site.siteMetadata?.title || "Title";

  /* ──────────────────────────────────────────────────────────────
     State
     ────────────────────────────────────────────────────────────── */

  const [showIntro, setShowIntro] = React.useState(true);
  const [contentVisible, setContentVisible] = React.useState(false);
  const [headerVisible, setHeaderVisible] = React.useState(false);

  // iOS: scrolling while intro is up can be ignored; do it after reveal/unlock
  const pendingScrollToFirstRef = React.useRef(false);

  /* ──────────────────────────────────────────────────────────────
     Initial mount logic (refresh handling)
     ────────────────────────────────────────────────────────────── */

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    // Prevent browser restoring scroll position
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const isReload = wasReloadNavigation();

    if (isReload) {
      // Treat refresh as a new session
      window.sessionStorage.removeItem(INTRO_KEY);

      // iOS: don't rely on immediate scroll (intro-lock may block it)
      pendingScrollToFirstRef.current = true;
    }

    const hasSeenIntro = window.sessionStorage.getItem(INTRO_KEY) === "1";

    if (hasSeenIntro) {
      setShowIntro(false);
      setContentVisible(true);
      setHeaderVisible(true);
      document.body.classList.remove("intro-lock");

      // If we reloaded and intro was skipped, still force top/first panel.
      if (pendingScrollToFirstRef.current) {
        pendingScrollToFirstRef.current = false;
        lockToFirstWorkPanel();
      }
    } else {
      document.body.classList.add("intro-lock");
    }
  }, []);

  /* ──────────────────────────────────────────────────────────────
     Replay handler (triggered by Header)
     ────────────────────────────────────────────────────────────── */

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const handleRequestReplay = () => {
      // Prevent browser restoring scroll position
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }

      // Treat as fresh session
      window.sessionStorage.removeItem(INTRO_KEY);

      // Mark that we must scroll once the intro unlocks/reveal begins
      pendingScrollToFirstRef.current = true;

      // Reset layout state
      document.body.classList.add("intro-lock");
      setContentVisible(false);
      setHeaderVisible(false);
      setShowIntro(true);

      // Let pages reset anything that depends on intro lifecycle
      window.setTimeout(() => {
        window.requestAnimationFrame(() => {
          window.dispatchEvent(new Event(INTRO_REPLAY_EVENT));
        });
      }, 50);
    };

    window.addEventListener(INTRO_REQUEST_REPLAY_EVENT, handleRequestReplay);
    return () =>
      window.removeEventListener(INTRO_REQUEST_REPLAY_EVENT, handleRequestReplay);
  }, []);

  /* ──────────────────────────────────────────────────────────────
     iOS: bfcache can restore scroll AFTER initial effects
   ────────────────────────────────────────────────────────────── */
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const onPageShow = (e) => {
      if (!e.persisted) return;

      // If intro is up, defer the scroll until reveal/unlock
      if (document.body.classList.contains("intro-lock")) {
        pendingScrollToFirstRef.current = true;
        return;
      }

      lockToFirstWorkPanel();
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  /* ──────────────────────────────────────────────────────────────
     iOS: disable pinch zoom
   ────────────────────────────────────────────────────────────── */
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    // iOS-only pinch zoom prevention
    const preventGesture = (e) => {
      e.preventDefault();
    };

    // These events exist on iOS Safari
    document.addEventListener("gesturestart", preventGesture, { passive: false });
    document.addEventListener("gesturechange", preventGesture, { passive: false });
    document.addEventListener("gestureend", preventGesture, { passive: false });

    // Extra guard: some iOS versions trigger zoom via touchmove with scale
    const preventPinch = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };
    document.addEventListener("touchmove", preventPinch, { passive: false });

    return () => {
      document.removeEventListener("gesturestart", preventGesture);
      document.removeEventListener("gesturechange", preventGesture);
      document.removeEventListener("gestureend", preventGesture);
      document.removeEventListener("touchmove", preventPinch);
    };
  }, []);

  /* ──────────────────────────────────────────────────────────────
     Called when intro finishes animation
     ────────────────────────────────────────────────────────────── */

  const finishIntro = React.useCallback(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(INTRO_KEY, "1");
      document.body.classList.remove("intro-lock");

      // Used by scroll hint ignore timing
      window.__INTRO_FINISHED_AT = Date.now();

      // Notify pages intro is done
      window.dispatchEvent(new Event(INTRO_FINISHED_EVENT));

      // iOS: ensure we end at the first panel after unlock
      if (pendingScrollToFirstRef.current) {
        pendingScrollToFirstRef.current = false;
        lockToFirstWorkPanel();
      }
    }

    setShowIntro(false);
  }, []);

  /* ──────────────────────────────────────────────────────────────
     Reveal underlying content while intro fades out
     ────────────────────────────────────────────────────────────── */

  const startRevealUnderOverlay = React.useCallback(() => {
    requestAnimationFrame(() => {
      setContentVisible(true);

      // Once content starts revealing, force the jump if requested
      if (pendingScrollToFirstRef.current) {
        pendingScrollToFirstRef.current = false;
        lockToFirstWorkPanel();
      }

      window.setTimeout(() => {
        setHeaderVisible(true);
      }, HEADER_DELAY_MS);
    });
  }, []);

  /* ──────────────────────────────────────────────────────────────
     Render
     ────────────────────────────────────────────────────────────── */

  return (
    <ContactModalProvider>
      {showIntro && (
        <IntroOverlay
          text="Kika Karadi"
          onStartExit={startRevealUnderOverlay}
          onFinish={finishIntro}
        />
      )}

      <div className={`intro-content ${contentVisible ? "is-visible" : ""}`}>
        <div className={`intro-header ${headerVisible ? "is-visible" : ""}`}>
          <Header siteTitle={siteTitle} />
        </div>

        <main className="site-main">{children}</main>
        <ContactModal />
      </div>
    </ContactModalProvider>
  );
}
